<!-- The table component to display the internal Atlas jobs -->
<template>
  <b-card 
    class="job-queue__card"
  >  
    <no-content
      :show="!currentData"
      text="No Data Available"
    />
    <filter-component 
      v-if="currentData"
      :options="options"
    />
    <div
      v-if="currentData"
      class="table-wrapper"
    >
      <b-table 
        ref="table" 
        :per-page="perPage"
        :current-page="page.currentPage"
        :items="currentData"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        sort-icon-left
        bordered
        responsive
        sticky-header="70vh"
      >
        <template #cell(status)="row">
          <span :class="row.item.status === 'Successful' ? 'text-success' : (row.item.status === 'Failed' ? 'text-danger' : '')">
            {{ row.item.status }}
          </span>
        </template>
        <template #cell(actions)="row">
          <b-button
            v-if="row.item.status === 'Running' || row.item.status === 'Not Started'" 
            :disabled="row.item.isCancelled"
            variant="danger" 
            size="sm" 
            @click="requestCancelJob(row.item)"
          >
            Cancel
          </b-button>
        </template>
      </b-table>

      <no-content
        :show="filteredTableData && filteredTableData.length === 0"
        text="There are no records matching your request"
      />
        
      <div class="d-flex justify-content-between main-header-wrapper">
        <div class="d-flex align-items-center">
          <pagination-summary
            v-if=" currentData.length > 0"
            :total="currentData.length"
            :page="page.currentPage"
            :per-page="perPage"
          />
          <b-form-select
            v-model="perPage"
            class="per-page-select ml-2"
            :options="perPageOptions"
            @change="onPerPageChange"
          />
        </div>
        <b-pagination
          v-if=" currentData.length > perPage"
          v-model="page.currentPage"
          class="mb-0"
          :total-rows="currentData.length"
          :per-page="perPage"
          @input="onCurrentPageChange"
        />
      </div>
    </div>
  </b-card>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import PaginationSummary from '@/core/components/complex-table/components/PaginationSummary.vue';
import FilterComponent from './FilterComponent.vue';
import NoContent from '@/core/components/NoContent.vue';

const {
  mapState: jobQueueMapState,
  mapActions: jobQueueMapActions,
} = createNamespacedHelpers('monitoring');

const fields = [
  { key: 'jobId', label: 'Job ID', sortable: true },
  { key: 'predecessorJobId', label: 'Predecessor Job ID', sortable: true },
  { key: 'parentJobId', label: 'Parent Job ID', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'operationKey', label: 'Operation Key', sortable: true },
  { key: 'operationCategory', label: 'Operation Category', sortable: true },
  { key: 'description', label: 'Description', sortable: true },
  { key: 'createdDateTime', label: 'Created Date Time', sortable: true },
  { key: 'startedDateTime', label: 'Started Date Time', sortable: true },
  { key: 'isBlocked', label: 'Is Blocked', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

export default {
  name: 'JobQueueTable',
  components: {
    PaginationSummary,
    FilterComponent,
    NoContent,
  },
  data() {
    return {
      selectedRow: null,
      showDrawer: false,
      sortBy: 'createdDateTime',
      sortDesc: false,
      sortDirection: 'asc',
      perPage: 10,
      perPageOptions: [10, 20, 30, 50, 100],
      fields: fields,
      options: fields
        .filter(field => field.key !== 'actions')
        .map(field => ({ text: field.label, value: field.key })),
      validationRulesData: [],
      intlInstance: null,
    };
  },
  computed: {
    ...jobQueueMapState({
      tableData: state => state.jobQueue.data,
      filteredTableData: state => state.jobQueue.filteredData,
      page: state => state.jobQueue.page,
    }),
    currentData(){
      return this.filteredTableData || this.tableData;
    },
  },
  methods: {
    ...jobQueueMapActions(['getJobQueueData', 'cancelJob']),    
    onPerPageChange() {
      this.page.currentPage = 1;
      this.$emit(
        'on-pagination-change',
        {
          perPage: this.perPage,
          currentPage: this.page.currentPage,
        },
      );
    },
    onCurrentPageChange() {
      this.$emit(
        'on-pagination-change',
        {
          perPage: this.perPage,
          currentPage: this.page.currentPage,
        });
    },
    requestCancelJob(job) {
      this.cancelJob(job.jobId);
    },
  },
  
  async mounted () {
    await this.getJobQueueData(); // Fetch the data when the component is mounted
    this.intervalId = setInterval(this.getJobQueueData, 5000); // Call every 5 seconds to refresh the data in the table
  },
  beforeDestroy() {
    clearInterval(this.intervalId); // Clear interval when component is destroyed
  },
};
</script>

<style>
.job-queue__card {
  height: 100%;
  overflow: auto;
}

.pagination-summary {
  white-space: nowrap;
}

.table.b-table > thead > tr > [aria-sort].b-table-sort-icon-left {
  background-position: left calc(0.75rem / 2) top calc(1rem) !important;
}

.table thead th {
  vertical-align: top !important;
  position: sticky !important;
}

.table-wrapper {
  max-height: 90vh;
  overflow-y: auto;
}
</style>